import React, { useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import DancerThumbnail from './DancerThumbnail';
import { findScriptDuplicates, pickPrimary, hasGreek } from './utils/scriptDuplicates';

/**
 * "Greek ⇄ English" tab of the Merge Dancers tool: the same dancer stored once with Greek letters
 * and once with Latin letters. Merging itself is done by the tool's existing merge routine
 * (onMerge), which renames results, moves profiles, records history for unmerge, etc.
 */
export default function MergeScriptsTab({ allDancers, user, onMerge }) {
  const [filter, setFilter] = useState('all'); // all | same | likely
  const [selected, setSelected] = useState({}); // groupId → Set(name) for "likely" groups
  const [profileInfo, setProfileInfo] = useState({}); // name → { claimed, photo }
  const [search, setSearch] = useState('');

  const groups = useMemo(() => findScriptDuplicates(allDancers), [allDancers]);

  // Which of these dancers have a claimed profile / photo – the merge tool deletes the other
  // profiles' documents, so such a dancer must be the one that survives.
  useEffect(() => {
    const missing = [...new Set(groups.flatMap((g) => g.dancers.map((d) => d.name)))].filter((n) => !(n in profileInfo));
    if (!missing.length) return undefined;
    let cancelled = false;
    (async () => {
      const found = {};
      for (let i = 0; i < missing.length; i += 25) {
        await Promise.all(missing.slice(i, i + 25).map(async (name) => {
          try {
            const [prof, dancer] = await Promise.all([getDoc(doc(db, 'dancerProfiles', name)), getDoc(doc(db, 'dancers', name))]);
            const p = prof.exists() ? prof.data() : {};
            const d = dancer.exists() ? dancer.data() : {};
            found[name] = { claimed: !!(p.profileClaimed || d.profileClaimed || p.claimedBy || d.claimedBy), photo: !!(p.profilePicture || d.profilePicture) };
          } catch (e) {
            found[name] = {};
          }
        }));
        if (cancelled) return;
      }
      setProfileInfo((prev) => ({ ...prev, ...found }));
    })();
    return () => { cancelled = true; };
  }, [groups, profileInfo]);

  const visible = groups.filter((g) => {
    if (filter !== 'all' && g.kind !== filter) return false;
    if (!search.trim()) return true;
    const t = search.toLowerCase();
    return g.dancers.some((d) => d.name.toLowerCase().includes(t) || (d.studios || []).some((s) => String(s).toLowerCase().includes(t)));
  });
  const counts = { same: groups.filter((g) => g.kind === 'same').length, likely: groups.filter((g) => g.kind === 'likely').length };

  const toggle = (groupId, name) => setSelected((prev) => {
    const next = new Set(prev[groupId] || []);
    if (next.has(name)) next.delete(name); else next.add(name);
    return { ...prev, [groupId]: next };
  });

  const mergeList = (list) => {
    const claimed = list.filter((d) => profileInfo[d.name] && profileInfo[d.name].claimed);
    if (claimed.length > 1) {
      window.alert(`${claimed.map((d) => d.name).join(' and ')} both have a claimed profile.\n\nMerging them would delete one claimed profile, so this needs to be sorted out manually.`);
      return;
    }
    // first = the profile that survives (claimed › has photo › Latin spelling › most results)
    onMerge(pickPrimary(list, profileInfo));
  };

  return (
    <div>
      <p className="description">
        Dancers stored twice — once with <strong>Greek letters</strong> and once with <strong>Latin (English) letters</strong>.
        <strong> Same name</strong> = identical once spelling is ignored (e.g. <em>Zoi Pratti</em> = <em>Ζωή Πράττη</em>, also
        &ldquo;Greeklish&rdquo; such as <em>GIWRGOS</em> = <em>Γιώργος</em>). <strong>Likely</strong> = very similar but not identical, so you choose
        which ones to merge. The profile with a claimed account or photo is kept automatically.
      </p>

      <div className="controls">
        <div className="search-controls">
          <input className="search-input" placeholder="Search a name or studio…" value={search} onChange={(e) => setSearch(e.target.value)} />
          <label className="checkbox-label">
            Show:&nbsp;
            <select value={filter} onChange={(e) => setFilter(e.target.value)}>
              <option value="all">All ({groups.length})</option>
              <option value="same">Same name ({counts.same})</option>
              <option value="likely">Likely ({counts.likely})</option>
            </select>
          </label>
        </div>
      </div>

      {!user && <div className="warning">⚠️ You must be logged in to perform merge operations.</div>}

      {visible.length === 0 ? (
        <div className="no-results">🎉 No Greek / English duplicates{filter !== 'all' || search ? ' match this filter' : ''}.</div>
      ) : (
        <div className="dancers-grid">
          {visible.map((g) => {
            const sel = selected[g.id] || new Set();
            const chosen = g.dancers.filter((d) => sel.has(d.name));
            const ordered = pickPrimary(g.dancers, profileInfo);
            const keeper = ordered[0];
            return (
              <div key={g.id} className="duplicate-group">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.4rem' }}>
                  <h4 style={{ margin: 0 }}>
                    {g.kind === 'same' ? '🟢 Same name' : `🟡 Likely match · ${g.score}%`}
                  </h4>
                  {g.kind === 'same' ? (
                    <button
                      onClick={() => mergeList(g.dancers)}
                      disabled={!user}
                      style={{ padding: '0.3rem 0.9rem', background: '#2e7d32', color: '#fff', border: 'none', borderRadius: 5, cursor: user ? 'pointer' : 'not-allowed', fontWeight: 'bold', fontSize: '0.85rem' }}
                    >
                      🔀 Merge into “{keeper.name}”
                    </button>
                  ) : (
                    <button
                      onClick={() => mergeList(chosen)}
                      disabled={!user || chosen.length < 2}
                      style={{ padding: '0.3rem 0.9rem', background: chosen.length >= 2 ? '#2e7d32' : '#aaa', color: '#fff', border: 'none', borderRadius: 5, cursor: user && chosen.length >= 2 ? 'pointer' : 'not-allowed', fontWeight: 'bold', fontSize: '0.85rem' }}
                    >
                      {chosen.length >= 2 ? `🔀 Merge ${chosen.length} selected` : 'Select at least 2 to merge'}
                    </button>
                  )}
                </div>

                <div className="group-dancers">
                  {g.dancers.map((d) => {
                    const info = profileInfo[d.name] || {};
                    const isSelected = g.kind === 'same' ? true : sel.has(d.name);
                    return (
                      <div
                        key={d.name}
                        className={`dancer-card ${isSelected ? 'selected' : ''}`}
                        onClick={g.kind === 'likely' ? () => toggle(g.id, d.name) : undefined}
                        style={g.kind === 'likely' ? { cursor: 'pointer' } : undefined}
                      >
                        <div className="dancer-header">
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <DancerThumbnail dancerName={d.name} size={36} showPlaceholder />
                            <h4>{d.name}</h4>
                          </div>
                          <div className="header-info">
                            {d.similarity !== undefined && <div className="similarity-badge">{d.similarity}% match</div>}
                            {g.kind === 'likely' && <div style={{ fontSize: '1.3rem' }}>{sel.has(d.name) ? '✅' : '⬜'}</div>}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '4px 0' }}>
                          <span style={{ fontSize: '0.72rem', padding: '1px 8px', borderRadius: 10, background: hasGreek(d.name) ? '#e3f2fd' : '#f3e5f5' }}>
                            {hasGreek(d.name) ? 'Ελληνικά' : 'English'}
                          </span>
                          {info.claimed && <span style={{ fontSize: '0.72rem', padding: '1px 8px', borderRadius: 10, background: '#e8f5e9', color: '#2e7d32' }}>✅ claimed profile</span>}
                          {info.photo && <span style={{ fontSize: '0.72rem', padding: '1px 8px', borderRadius: 10, background: '#fff8e1' }}>📷 photo</span>}
                          {d.name === keeper.name && g.kind === 'same' && <span style={{ fontSize: '0.72rem', padding: '1px 8px', borderRadius: 10, background: '#e8eaf6' }}>kept</span>}
                        </div>
                        <div className="dancer-details">
                          <p><strong>Studios:</strong> {(d.studios || []).join(', ') || '—'}</p>
                          <p><strong>Competitions:</strong> {(d.competitions || []).length} · <strong>Results:</strong> {d.totalResults}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
